import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

interface Company {
  id: string;
  name: string;
  description: string;
  website: string;
  location: string;
  size: string;
  industry: string;
  logoUrl: string;
  rating: number;
  reviewCount: number;
  jobsCount: number;
  benefits: string[];
  culture: string[];
}

const CompanyProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // TODO: Fetch real company data from backend /companies/:id endpoint
  // This entire page uses placeholder data until Company model & API is implemented
  const [company] = useState<Company>({
    id: id || '1',
    name: 'Company Profile',
    description: 'Company profile feature coming soon. This page will display detailed information about employers.',
    website: '',
    location: '',
    size: '',
    industry: '',
    logoUrl: 'https://via.placeholder.com/120',
    rating: 0,
    reviewCount: 0,
    jobsCount: 0,
    benefits: [],
    culture: []
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-6">
            <img
              src={company.logoUrl}
              alt={`${company.name} logo`}
              className="w-24 h-24 rounded-2xl object-cover"
            />
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--twc-ink)' }}>
                {company.name}
              </h1>
              <p className="text-lg text-gray-600 mb-4">{company.description}</p>
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>📍 {company.location}</span>
                <span>👥 {company.size}</span>
                <span>🏢 {company.industry}</span>
                <span>🌐 <a href={company.website} className="text-blue-600 hover:underline">{company.website}</a></span>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">⭐</span>
                <span className="text-2xl font-bold">{company.rating}</span>
                <span className="text-gray-500">({company.reviewCount} reviews)</span>
              </div>
              <p className="text-sm text-gray-600">{company.jobsCount} open positions</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                About {company.name}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {company.description}
              </p>
              <p className="text-gray-700 leading-relaxed">
                Founded on principles of academic excellence and innovation, we continue to push the boundaries of knowledge while maintaining our commitment to student success and research advancement.
              </p>
            </div>

            {/* Culture & Values */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                Culture & Values
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {company.culture.map((value, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--twc-paper)' }}>
                    <span className="text-lg">✨</span>
                    <span className="text-gray-700">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                Benefits & Perks
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {company.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--twc-paper)' }}>
                    <span className="text-lg">🎁</span>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                Company Stats
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Rating</span>
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-400">⭐</span>
                    <span className="font-medium">{company.rating}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Reviews</span>
                  <span className="font-medium">{company.reviewCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Open Jobs</span>
                  <span className="font-medium">{company.jobsCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Company Size</span>
                  <span className="font-medium">{company.size}</span>
                </div>
              </div>
            </div>

            {/* Recent Reviews */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                Recent Reviews
              </h3>
              {/* TODO: Fetch real reviews from backend /companies/:id/reviews */}
              <div className="text-center py-4 text-gray-500">
                <p>Reviews feature coming soon</p>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                Actions
              </h3>
              <div className="space-y-3">
                <button className="w-full py-3 px-4 rounded-lg text-white font-medium transition-all hover:opacity-90" style={{ backgroundColor: 'var(--twc-tiger)' }}>
                  View All Jobs ({company.jobsCount})
                </button>
                <button className="w-full py-3 px-4 rounded-lg border font-medium transition-all hover:opacity-80" style={{ borderColor: 'var(--twc-timberwolf)', color: 'var(--twc-feldgrau)' }}>
                  Follow Company
                </button>
                <button className="w-full py-3 px-4 rounded-lg border font-medium transition-all hover:opacity-80" style={{ borderColor: 'var(--twc-timberwolf)', color: 'var(--twc-feldgrau)' }}>
                  Write Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfilePage;
